import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useCart } from "../context/CartContext";
import { PageHeader } from "../components/PageHeader";
import { Reveal } from "../components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full border border-stone-900/20 bg-transparent px-4 py-3.5 text-sm text-stone-900 placeholder:text-stone-400 focus:border-emerald-900 focus:outline-none transition-colors duration-300";

export default function Checkout() {
  const { items, subtotal, clearCart } = useCart();
  const navigate = useNavigate();
  const [placing, setPlacing] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    address1: "",
    address2: "",
    city: "",
    postcode: "",
    country: "United Kingdom",
  });

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const shippingCost = 0;
  const total = subtotal + shippingCost;

  const placeOrder = async (e) => {
    e.preventDefault();
    setPlacing(true);
    try {
      const { data } = await axios.post(`${API}/orders`, {
        customer: form,
        items: items.map((i) => ({ name: i.name, qty: i.qty, price: i.price })),
        subtotal,
        shipping: shippingCost,
        total,
      });
      clearCart();
      navigate(`/order-success/${data.id}`);
    } catch {
      toast.error("Could not place the demo order. Please try again.");
    } finally {
      setPlacing(false);
    }
  };

  if (items.length === 0) {
    return (
      <div data-testid="checkout-empty">
        <PageHeader eyebrow="Checkout" title="Your bag is empty." body="Add something considered to it first." />
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-8 lg:px-16">
          <Link
            to="/shop"
            data-testid="checkout-empty-shop-link"
            className="inline-block bg-stone-900 px-9 py-4 font-mono text-[11px] font-medium uppercase tracking-[0.25em] text-stone-50 transition-colors duration-300 hover:bg-emerald-900"
          >
            Go to Shop
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="checkout-page">
      <PageHeader eyebrow="Checkout" title="Almost there." />
      <section className="mx-auto max-w-7xl px-4 pb-24 sm:px-8 lg:px-16">
        <Reveal>
          <div
            data-testid="demo-checkout-banner"
            className="mb-12 border border-emerald-900/30 bg-[#EEF3EF] px-6 py-4 font-mono text-[11px] uppercase tracking-[0.2em] text-emerald-900"
          >
            Demo checkout — no payment will be taken and no card details are collected.
          </div>
        </Reveal>

        <form onSubmit={placeOrder} data-testid="checkout-form" className="grid gap-16 lg:grid-cols-5">
          <Reveal className="space-y-6 lg:col-span-3">
            <p className="font-mono text-[11px] font-medium uppercase tracking-[0.25em] text-stone-900">
              Delivery details
            </p>
            <div className="grid gap-6 sm:grid-cols-2">
              <div>
                <label htmlFor="ck-name" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.2em] text-stone-500">Full name</label>
                <input id="ck-name" data-testid="checkout-name-input" required value={form.name} onChange={set("name")} className={inputCls} placeholder="Jane Smith" />
              </div>
              <div>
                <label htmlFor="ck-email" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.2em] text-stone-500">Email</label>
                <input id="ck-email" type="email" data-testid="checkout-email-input" required value={form.email} onChange={set("email")} className={inputCls} placeholder="you@example.co.uk" />
              </div>
            </div>
            <div>
              <label htmlFor="ck-address1" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.2em] text-stone-500">Address line 1</label>
              <input id="ck-address1" data-testid="checkout-address1-input" required value={form.address1} onChange={set("address1")} className={inputCls} placeholder="House number and street" />
            </div>
            <div>
              <label htmlFor="ck-address2" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.2em] text-stone-500">Address line 2 (optional)</label>
              <input id="ck-address2" data-testid="checkout-address2-input" value={form.address2} onChange={set("address2")} className={inputCls} placeholder="Flat, building, etc." />
            </div>
            <div className="grid gap-6 sm:grid-cols-2">
              <div>
                <label htmlFor="ck-city" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.2em] text-stone-500">Town / City</label>
                <input id="ck-city" data-testid="checkout-city-input" required value={form.city} onChange={set("city")} className={inputCls} placeholder="London" />
              </div>
              <div>
                <label htmlFor="ck-postcode" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.2em] text-stone-500">Postcode</label>
                <input id="ck-postcode" data-testid="checkout-postcode-input" required value={form.postcode} onChange={set("postcode")} className={inputCls} placeholder="SW1A 1AA" />
              </div>
            </div>
            <div>
              <label htmlFor="ck-country" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.2em] text-stone-500">Country</label>
              <input id="ck-country" data-testid="checkout-country-input" value={form.country} disabled className={`${inputCls} opacity-60`} />
            </div>
          </Reveal>

          <Reveal delay={0.12} className="lg:col-span-2">
            <aside data-testid="order-summary" className="border border-stone-900/10 bg-[#F3EFE6] p-8 lg:sticky lg:top-28">
              <p className="font-mono text-[11px] font-medium uppercase tracking-[0.25em] text-stone-900">Order summary</p>
              <ul className="mt-6 space-y-4">
                {items.map((i) => (
                  <li key={i.id} className="flex items-baseline justify-between gap-4 text-sm">
                    <span className="text-stone-700">
                      {i.name} <span className="font-mono text-xs text-stone-500">× {i.qty}</span>
                    </span>
                    <span className="font-mono text-stone-900">£{(i.price * i.qty).toFixed(2)}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-6 space-y-3 border-t border-stone-900/10 pt-6 text-sm">
                <div className="flex justify-between text-stone-600">
                  <span>Subtotal</span>
                  <span className="font-mono">£{subtotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-stone-600">
                  <span>UK delivery</span>
                  <span className="font-mono">{shippingCost === 0 ? "Free" : `£${shippingCost.toFixed(2)}`}</span>
                </div>
                <div className="flex justify-between border-t border-stone-900/10 pt-4">
                  <span className="font-medium text-stone-900">Total</span>
                  <span data-testid="checkout-total" className="font-serif text-2xl font-light text-stone-900">
                    £{total.toFixed(2)}
                  </span>
                </div>
              </div>
              <button
                type="submit"
                data-testid="place-order-button"
                disabled={placing}
                className="mt-8 w-full bg-stone-900 px-8 py-4 font-mono text-[11px] font-medium uppercase tracking-[0.25em] text-stone-50 transition-colors duration-300 hover:bg-emerald-900 disabled:opacity-50"
              >
                {placing ? "Placing…" : "Place Demo Order"}
              </button>
            </aside>
          </Reveal>
        </form>
      </section>
    </div>
  );
}
